import React, { memo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import useSimulationStore from '../../../store/simulationStore';
import ChemicalFormula from '../ChemicalFormula';
import {
    unifiedReactionsContainer,
    unifiedReactionItem,
    unifiedSpeciesLeft,
    unifiedSpeciesRight,
    unifiedReactionButton,
    unifiedReactionButtonFocused,
    unifiedControlSectionHeader,
    unifiedControlSectionIcon,
    unifiedControlSectionContent,
    unifiedControlGroup,
    unifiedControlLabel,
    unifiedControlColorPicker,
    inheritanceIndicator,
    inheritanceArrow,
    customIndicator,
} from '../unifiedPanelStyles';

const UnifiedReactionsPanel: React.FC = memo(() => {
    const {
        activeChemistryConfig,
        reactionFocus,
        focusReaction,
        unfocusReaction,
        focusElement,
        availableReactions,
        availableSpecies,
        reactionColors,
        setReactionColor,
        speciesColors,
        setSpeciesColor,
        elementColors,
        setElementColor
    } = useSimulationStore(
        state => ({
            activeChemistryConfig: state.activeChemistryConfig,
            reactionFocus: state.reactionFocus,
            focusReaction: state.focusReaction,
            unfocusReaction: state.unfocusReaction,
            focusElement: state.focusElement,
            availableReactions: state.availableReactions,
            availableSpecies: state.availableSpecies,
            reactionColors: state.reactionColors,
            setReactionColor: state.setReactionColor,
            speciesColors: state.speciesColors,
            setSpeciesColor: state.setSpeciesColor,
            elementColors: state.elementColors,
            setElementColor: state.setElementColor
        }),
        shallow
    );

    const [isExpanded, setIsExpanded] = useState(true);

    // Helper function to get species name from ID
    const getSpeciesName = (speciesId: string) => {
        const species = availableSpecies.find(s => s.id === speciesId);
        return species ? species.name : speciesId;
    };

    return (
        <div style={unifiedReactionsContainer}>
            <button
                style={unifiedControlSectionHeader}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span style={unifiedControlSectionIcon}>
                    {isExpanded ? '▼' : '▶'}
                </span>
                Reactions ({activeChemistryConfig.reactions.length})
            </button>

            {isExpanded && (
                <div style={unifiedControlSectionContent}>
                    <div style={unifiedControlGroup}>
                        <label style={unifiedControlLabel}>
                            Reaction colors override species and element colors
                        </label>
                    </div>

                    {activeChemistryConfig.reactions.map(reaction => {
                        const isFocused = reactionFocus[reaction.id] || false;
                        // Find the actual reaction definition to get the proper formula
                        const reactionDef = availableReactions.find(r => r.id === reaction.reactionDefId);

                        let displayName = `Reaction ${reaction.id}`;
                        if (reactionDef) {
                            const reactants = reactionDef.reactants.map(r => `${r.coefficient}${getSpeciesName(r.speciesId)}`).join(' + ');
                            const products = reactionDef.products.map(p => `${p.coefficient}${getSpeciesName(p.speciesId)}`).join(' + ');
                            displayName = `${reactants} → ${products}`;
                        }

                        // Get reaction color from store
                        const reactionColor = reactionColors[reaction.id] || '#ffffff';
                        const hasReactionColor = !!reactionColors[reaction.id];

                        return (
                            <div key={reaction.id} style={unifiedReactionItem}>
                                <div style={unifiedSpeciesLeft}>
                                    <button
                                        style={{
                                            ...unifiedReactionButton,
                                            ...(isFocused ? unifiedReactionButtonFocused : {})
                                        }}
                                        onClick={() => isFocused ? unfocusReaction(reaction.id) : focusReaction(reaction.id)}
                                        title="Click to focus/unfocus this reaction"
                                    >
                                        <ChemicalFormula formula={displayName} />
                                    </button>
                                </div>

                                <div style={unifiedSpeciesRight}>
                                    <input
                                        type="color"
                                        value={reactionColor}
                                        onChange={(e) => {
                                            const newColor = e.target.value;
                                            setReactionColor(reaction.id, newColor);
                                            // Auto-focus the reaction when color is set
                                            if (!isFocused) {
                                                focusReaction(reaction.id);
                                            }

                                            // Focus all elements involved in this reaction
                                            if (reactionDef) {
                                                const allSpeciesInReaction = [
                                                    ...reactionDef.reactants.map(r => r.speciesId),
                                                    ...reactionDef.products.map(p => p.speciesId)
                                                ];

                                                // Get all unique elements from all species in this reaction
                                                const allElements = new Set<string>();
                                                allSpeciesInReaction.forEach(speciesId => {
                                                    const species = availableSpecies.find(s => s.id === speciesId);
                                                    if (species) {
                                                        const elements = species.name.match(/[A-Z][a-z]?/g) || [];
                                                        elements.forEach(element => allElements.add(element));
                                                    }
                                                });

                                                // Focus all elements involved in this reaction
                                                allElements.forEach(element => {
                                                    focusElement(element);
                                                });

                                                allSpeciesInReaction.forEach(speciesId => {
                                                    // Clear species color so it inherits from reaction
                                                    if (speciesColors[speciesId]) {
                                                        setSpeciesColor(speciesId, '');
                                                    }

                                                    // Clear element colors for elements in this species
                                                    const species = availableSpecies.find(s => s.id === speciesId);
                                                    if (species) {
                                                        const elements = species.name.match(/[A-Z][a-z]?/g) || [];
                                                        elements.forEach(element => {
                                                            if (elementColors[element]) {
                                                                setElementColor(element, '');
                                                            }
                                                        });
                                                    }
                                                });
                                            }
                                        }}
                                        style={unifiedControlColorPicker}
                                        title="Reaction color (overrides species and element colors)"
                                    />

                                    <div
                                        style={inheritanceIndicator}
                                        title={hasReactionColor
                                            ? `Custom reaction color set. Click to change.`
                                            : `Using default color. Click to set custom reaction color.`
                                        }
                                    >
                                        {hasReactionColor ? (
                                            <span style={customIndicator}>●</span>
                                        ) : (
                                            <span style={inheritanceArrow}>↓</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

UnifiedReactionsPanel.displayName = 'UnifiedReactionsPanel';

export default UnifiedReactionsPanel;
